import {
	take,
	call,
	put,
	fork,
	all,
	takeLatest,
	select,
} from 'redux-saga/effects';
import { get } from 'lodash';

import { ApiClient } from '@shared/services';

import { saveData, removeData, getData } from '@/data/cache';
import { SHOW_TOAST } from '@/redux/ducks/toast/types';
import Constants from '@/utils/constants';
import { formatPermission } from '@/utils/permissions';
import {
	GET_USER_INFO,
	GET_USER_INFO_SUCCESS,
	GET_USER_INFO_ERROR,
	GET_USER_PERMISSIONS_SUCCESS,
	GET_USER_PERMISSIONS_ERROR,
	UPDATE_USER_GROUP,
	UPDATE_USER_GROUP_SUCCESS,
	UPDATE_USER_GROUP_ERROR,
	GET_USER_GROUP_LIST,
	GET_USER_GROUP_LIST_SUCCESS,
	GET_USER_GROUP_LIST_ERROR,
	UPDATE_USER_MID,
	UPDATE_USER_MID_ERROR,
	SAVE_MID_DATA,
} from '@/redux/ducks/login/types';
import { INIT_APP_ERROR, INIT_APP } from '@/redux/ducks/init/types';
import api from '@/data';
import { RESET_MANAGE_STORES } from '@/redux/ducks/manageStores/types';
import { RESET_USER_CONFIG } from '@/redux/ducks/userConfig/types';
import { filterUserGroupsByStatus } from '@/utils/helper';
import {
	SET_USER_ROLE,
	SET_USER_MERCHANT_DETAILS,
} from '@/redux/ducks/userProfile/types';

const getStoredPermissions = (state) => state.userAuth.user.permissions;

export function* refreshTokenNRetry(retryFn, payload, extras) {
	try {
		yield call(api.refreshToken);
		if (payload) {
			if (extras) {
				yield call(retryFn, payload, ...extras);
			} else {
				yield call(retryFn, payload);
			}
		} else {
			yield call(retryFn);
		}
	} catch (e) {
		console.log(e);
	}
}

// Function to get user's permission.
export function* getUserPermissions() {
	try {
		const response = yield call(api.getUserPermissions);
		const storedPermissions = yield call(
			getData,
			Constants.USER_PERMISSIONS_KEY,
		);
		const newPermissions = formatPermission(response.data);
		let consolidatedPermissions = { ...newPermissions };
		if (storedPermissions) {
			consolidatedPermissions = {
				...storedPermissions,
				...newPermissions,
			};
		}
		yield put({
			type: GET_USER_PERMISSIONS_SUCCESS,
			data: consolidatedPermissions,
		});
		// save user's permission in localstorage in USER_PERMISSIONS_KEY key
		yield call(
			saveData,
			Constants.USER_PERMISSIONS_KEY,
			consolidatedPermissions,
		);
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: GET_USER_PERMISSIONS_ERROR, error: error.data });
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* getUserInfo() {
	try {
		const response = yield call(api.getUserDetail);
		yield call(saveData, Constants.USER_INFO_KEY, response.data);
		yield put({ type: GET_USER_INFO_SUCCESS, data: response.data });
		yield put({
			type: SET_USER_MERCHANT_DETAILS,
			data: {
				merchantUserId: get(response, 'data.merchantUserId', ''),
			},
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: GET_USER_INFO_ERROR,
				error: error.data,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

// Function to watch all the API calls.
export function* userInfoWatcher() {
	while (true) {
		yield take(GET_USER_INFO);
		try {
			yield all([call(getUserPermissions), call(getUserInfo)]);
		} catch (error) {
			// handle user Info watcher error here
		}
	}
}

export function* updateUserGroup(payload) {
	try {
		const { groupId } = payload.payload;
		const response = yield call(api.updateUserGroup, groupId);
		yield call(getUserInfo);
		yield call(getUserPermissions);
		yield put({ type: UPDATE_USER_GROUP_SUCCESS, data: response.data });
	} catch (error) {
		yield put({ type: UPDATE_USER_GROUP_ERROR, error: error.data });
	}
}

export function* updateUserGroupWatcher() {
	while (true) {
		const payload = yield take(UPDATE_USER_GROUP);
		yield call(updateUserGroup, payload);
	}
}

const getUserGroupValue = (state) => get(state, 'userAuth.user.groups', []);
const checkGroupSelectionEnabled = (state) =>
	get(state, 'userAuth.user.groupSelection', false);

export function* getUserGroupList() {
	try {
		const isGroupSelectionEnabled = yield select(checkGroupSelectionEnabled);
		let userGroupValue = yield select(getUserGroupValue);
		if (userGroupValue.length === 0) {
			const loginResponse = JSON.parse(
				sessionStorage.getItem(Constants.LOGIN_RESPONSE_KEY) ?? '{}',
			);
			userGroupValue = get(loginResponse, 'data.groups', []);
		}
		if (isGroupSelectionEnabled) {
			const response = yield call(api.getGroupSelectionList);
			yield call(saveData, Constants.USER_GROUPS_LIST_KEY, response.data);
			yield put({
				type: GET_USER_GROUP_LIST_SUCCESS,
				data: filterUserGroupsByStatus(response.data),
			});
		} else {
			const morphedData = [
				{
					merchantName: '', // Since we don't have merchant name at the time of init keeping it blank because need to keep this in sync with the userGroup selection API rsponse.
					userGroupNamespace: userGroupValue[0],
				},
			];
			yield call(saveData, Constants.USER_GROUPS_LIST_KEY, morphedData);
			yield put({ type: GET_USER_GROUP_LIST_SUCCESS, data: morphedData });

			// saves user groups list in localstorage
			yield put({ type: SAVE_MID_DATA, selectedMID: morphedData[0] });
			yield call(saveData, Constants.SELECTED_MID, morphedData[0]);
			yield put({
				type: INIT_APP,
			});
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: GET_USER_GROUP_LIST_ERROR, error: error.data });
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* getUserGroupListWatcher() {
	yield takeLatest(GET_USER_GROUP_LIST, getUserGroupList);
}

// Function to clear local storage.
export function* clearStorage() {
	yield call(removeData, Constants.USER_PERMISSIONS_KEY);
	yield call(removeData, Constants.USER_INFO_KEY);
	yield call(removeData, Constants.MERCHANT_PROFILE_KEY);
	yield call(removeData, Constants.MERCHANT_LIST_KEY);
	yield call(removeData, Constants.TEST_MODE_AUTH_KEY);
	yield call(removeData, Constants.TEST_MODE_REFRESH_KEY);
	yield put({ type: RESET_USER_CONFIG });
}

export function* refreshSession() {
	yield call(clearStorage);

	// Reset states in redux store that are meant to be stored in session storage
	yield put({ type: RESET_MANAGE_STORES });
	yield put({ type: RESET_USER_CONFIG });
}

export function* updateUserMID(groupId, merchantGroupName, midData, refresh) {
	try {
		const response = yield call(api.updateUserGroup, groupId);
		yield put({
			type: SET_USER_ROLE,
			data: {
				roleName: get(response, 'data.roleName'),
				roleId: get(response, 'data.roleId'),
			},
		});
		yield put({ type: SAVE_MID_DATA, selectedMID: midData });
		if (refresh) {
			yield call(refreshSession);
			yield call(() => window.location.reload());
		} else {
			yield put({
				type: INIT_APP,
			});
			ApiClient.reset();
		}
		if (midData) yield call(saveData, Constants.SELECTED_MID, midData);
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: UPDATE_USER_MID_ERROR, error: error.data });
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* updateUserMIDWatcher() {
	while (true) {
		const {
			payload: { groupId, merchantGroupName, midData, refresh },
		} = yield take(UPDATE_USER_MID);
		yield call(updateUserMID, groupId, merchantGroupName, midData, refresh);
	}
}

function* userApiSaga() {
	yield fork(userInfoWatcher);
	yield fork(updateUserGroupWatcher);
	yield fork(getUserGroupListWatcher);
	yield fork(updateUserMIDWatcher);
}

export default userApiSaga;
export { getStoredPermissions };
